import { Component, OnInit, OnDestroy, Input, SimpleChanges } from '@angular/core';
import OAuthInfo from '@arcgis/core/identity/OAuthInfo';
import esriId from '@arcgis/core/identity/IdentityManager';
import { Subscription } from 'rxjs';
import { ArcGISUserService } from 'app/shared/services/arcgis-user-service';
import { ArcGISUserToken, CreateArcGISUserTokenRequest, GisServiceList } from 'app/shared/models/gis-service-list';
import HOME from 'assets/i18n/en-us.json';
import { Router } from '@angular/router';
import { GIS_SERVICES_LIST } from 'app/main';
import { normalizePortalUrl } from 'app/shared/constants/arcgis-constants';
declare global {
    interface Window {
        oauthCallback?: (authorizationCode: string, isToken: boolean) => void;
    }
}
@Component({
    selector: 'app-arcgis-login-dialog',
    templateUrl: './arcgis-login-dialog.component.html',
    styleUrls: ['./arcgis-login-dialog.component.scss'],
})
export class ArcGISLoginDialogComponent implements OnInit, OnDestroy {

    gisOAuthInfo: OAuthInfo = new OAuthInfo();
    gisSubscription: Subscription;
    orgContentSubscription: Subscription;
    secondWindow: Window | null = null;
    public displaySignInButton: boolean;
    public userMessage: string;
    public displayUserName: string = '';
    @Input() userToken: ArcGISUserToken;
    @Input() customerId: number;

    constructor(
        private arcgisUserService: ArcGISUserService,
        private router: Router,
    ) {}

    ngOnInit() {
        this.gisSubscription = this.arcgisUserService.getGisClientId().subscribe((clientId: string) => {
            this.gisOAuthInfo.appId = clientId;
        });

        this.displaySignInButton = !(!!this.userToken);
        this.updateUserMessage();

        this.orgContentSubscription = this.arcgisUserService.organizationContentChanged.subscribe((content) => {
            if (content) {
                this.updateUserMessage();
            }
        });

        esriId.registerOAuthInfos([this.gisOAuthInfo]);

        const oauthCallback = (authorizationCode: string, isToken: boolean) => {
            if (authorizationCode !== null) {
                this.checkSignInStatus(authorizationCode, isToken);
            } else {
                this.secondWindow.close();
            }
        }

        window.oauthCallback = oauthCallback;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.userToken) {
            this.userToken = changes.userToken.currentValue;
            this.displaySignInButton = !(!!this.userToken);
            this.updateUserMessage();
        }
    }

    private updateUserMessage(): void {
        if (this.displaySignInButton) {
            this.userMessage = HOME.HOME.ARCGIS.SIGNED_OUT_USER_MESSAGE;
            this.displayUserName = '';
        } else {
            const userInfo = this.arcgisUserService.getStoredUserInfo();
            this.displayUserName = userInfo.fullName || userInfo.username || '';
            this.userMessage = HOME.HOME.ARCGIS.SIGNED_IN_USER_MESSAGE;
        }
    }

    async checkSignInStatus(authorization: string, isToken: boolean = false) {
        if (!isToken) {
            const token = await this.arcgisUserService.generateArcGISUserAuthToken(this.gisOAuthInfo, authorization);
            if (token && (token.access_token ?? '').trim().length > 0) {
                const arcgisService: GisServiceList | null = sessionStorage.getItem(GIS_SERVICES_LIST) ? JSON.parse(sessionStorage.getItem(GIS_SERVICES_LIST))?.find((s: GisServiceList) => s.serviceType === 'identity') : null;
                const enterpriseUrl: string | null = arcgisService?.url ? normalizePortalUrl(arcgisService.url) : null;

                const req: CreateArcGISUserTokenRequest = {
                    arcGISUserToken: token.access_token,
                    arcGISUserRefreshToken: token.refresh_token,
                    arcGISUserTokenExpiration: token.expires_in,
                    arcGISUserRefreshTokenExpiration: token.refresh_token_expires_in,
                    arcGISEnterpriseDomainUrl: enterpriseUrl,
                    arcGISEnterpriseAppId: enterpriseUrl ? arcgisService?.appId ?? null : null,
                };
                const customerId = enterpriseUrl ? this.customerId : undefined;
                this.storeArcGISToken(req, customerId);
            }
        }
        else {
            const req: CreateArcGISUserTokenRequest = {
                arcGISUserToken: authorization,
            };
            this.storeArcGISToken(req);
        }
    }

    signInOut() {
        if (this.displaySignInButton) {
            const authorizationEndpoint = this.arcgisUserService.generateArcGISLoginEndpoint(this.gisOAuthInfo);
            this.secondWindow = window.open(authorizationEndpoint, 'oauth-window', 'height=400,width=600,menubar=no,location=yes,resizable=yes,scrollbars=yes,status=yes');
        } else {
            const arcgisService: GisServiceList | null = sessionStorage.getItem(GIS_SERVICES_LIST) ? JSON.parse(sessionStorage.getItem(GIS_SERVICES_LIST))?.find((s: GisServiceList) => s.serviceType === 'identity') : null;
            const enterpriseUrl: string | null = arcgisService?.url ? normalizePortalUrl(arcgisService.url) : null;
            this.arcgisUserService.deleteUserCredToken(enterpriseUrl, enterpriseUrl ? this.customerId : undefined).subscribe({
                next: (response: boolean) => {
                    this.displaySignInButton = response;
                }
            });
        }
    }

    storeArcGISToken(request: CreateArcGISUserTokenRequest, customerId?: number) {
        this.arcgisUserService.storeUserCredToken(request, customerId).subscribe({
            next: (response: ArcGISUserToken) => {
                this.secondWindow.close();
            },
            error: (err) => {
                this.secondWindow.close();
            },
        });
    }    

    ngOnDestroy() {
        esriId.destroyCredentials();
        if (this.gisSubscription) {
            this.gisSubscription.unsubscribe();
        }
        if (this.orgContentSubscription) {
            this.orgContentSubscription.unsubscribe();
        }
    }
}
